import React, { useEffect, useState, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { Header } from '@backstage/core-components';
import 'ag-grid-enterprise';
import Loader from '../loader/Loader';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef} from '@backstage/core-plugin-api';
import '../NoteCustomstyle.css';

// Material UI dropdown
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const EC2Snapshots = () => {
  const gridRef = useRef<any>(null);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100vh" }), []);
  const gridStyle = useMemo(() => ({ height: 'calc(-200px + 80vh)', width: "100%", marginTop: '50px' }), []);
  const [rowData, setRowdata] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState('');
  const [tenantName, settenantName] = useState<string>(''); // dropdown state
  const [role, setRole] = useState<string>(''); // role state
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [tenantsFromDb, setTenantsFromDb] = useState<any[]>([]);
  const identityApi = useApi(identityApiRef)

  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await fetchApi.fetch(`${baseUrl}/tenants`);
      if (!response.ok) throw new Error("Failed to fetch tenants");
      return await response.json();
    } catch (e) {
      console.error("Error fetching tenants:", e);
      return [];
    }
  };

  const fetchTenant = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const id = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1] || null;

      console.log("ID ---------", id)
      return id
    } catch (err) {
      console.error('Failed to get tenantId:', err);
    }
  }; 

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenants = await fetchTenantsFromDb();
        const id = await fetchTenant()
        console.log(`orgName:${id}`)
        const tenantOrgName = localStorage.getItem(`orgName:${id}`);
        console.log('tenantOrgName------',tenantOrgName)
        if (tenantOrgName) setOrg(tenantOrgName);
        setTenantsFromDb(tenants);
        const baseurl = await discoveryApi.getBaseUrl('user');
        let responseData: any = [];

        try {
          const fetchresponse = await fetchApi.fetch(`${baseurl}/ec2snapshots`, {
            method: 'POST',
          });
          if (!fetchresponse.ok) {
            const errorText = await fetchresponse.text();
            throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
          }
          const response = await fetchresponse.json();
          responseData = response.data || response; // adjust based on API response
          setRole(response.role || ""); // store role if backend sends it
          console.log('Fetched data:', responseData);
        } catch (error) {
          console.error('Fetch or JSON parse error:', error);
        }

        setRowdata(responseData);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchData();
    let orgName = localStorage.getItem('orgName')
    if (orgName) {
      setOrg(orgName)
    }
  }, [])

  const columnDefs = useMemo(() => {
    const baseCols = [
    { headerName: "Tenant Name", field: "tenantName", headerTooltip: "Tenant Name", rowGroup: true },
    {
      headerName: "Account Id", field: "accountId", headerTooltip: "Account Id", rowGroup: true, valueFormatter: params => {
        if (params.node.rowIndex === params.api.getDisplayedRowCount() - 1 && !params.value) {
          return 'Total';
        }
        else {
          return params.value;
        }
      }
    },
    { headerName: "Region", field: "region", headerTooltip: "Region" },
    { headerName: 'Snapshot ID', field: 'snapshotId', headerTooltip: "Snapshot ID" },
    { headerName: 'Snapshot Time', field: 'snapshotTime', headerTooltip: "Snapshot Time" },
    {
      headerName: 'Total Snapshot Cost in $(per GB per month)', field: 'totalSnapshotCostPerMonth', headerTooltip: "Total Snapshot Cost in $(per GB per month)", aggFunc: 'sum', valueFormatter: params => {
        const value = parseFloat(params.value);
        if (!isNaN(value))
          return value.toFixed(2);
      },
    },
    {
      headerName: 'Cost saving if Deleted in $(per GB per month)', field: 'totalCostSavingsDeleted', aggFunc: 'sum', headerTooltip: 'Cost saving if Deleted in $(per GB per month)', valueFormatter: params => {
        const value = parseFloat(params.value);
        if (!isNaN(value))
          return value.toFixed(2);
      },
    },
    {
      headerName: 'Cost Saving if Archived in $(per GB per month)',
      field: 'totalCostSavingsArchived', aggFunc: 'sum', headerTooltip: 'Cost Saving if Archived in $(per GB per month)',
      valueFormatter: params => {
        const value = parseFloat(params.value);
        if (!isNaN(value)) return value.toFixed(2);
      },
    },
  ];
  
  return baseCols.map(col =>
    col.field === "tenantName"
      ? { ...col, hide: role != "SuperAdmin", rowGroup: role != "SuperAdmin" ? false : col.rowGroup }
      : col
  );
}, [role]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1, filter: true, sortable: true, resizable: true, enableRowGroup: true, tooltipShowDelay: 0, tooltipInteraction: true, columnMenu: "new"
    };
  }, []);

  const autoGroupColumnDef = {
    minWidth: 250,
    cellRendererParams: {
      footerValueGetter: (params) => {
        const isRootLevel = params.node.level === -1;
        if (isRootLevel) {
          return "Total";
        }
        return "Sub Total";
      },
    },
  }

  const sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      }
    ],
    defaultToolPanel: '',
  }


  const gridOptions = { suppressContextMenu: true };

  // filter based on tenant
  const filteredData = tenantName
    ? rowData.filter(row => String(row.tenantName) === String(tenantName))
    : rowData;

  // extract unique tenants
  const tenants = [...new Set(rowData.map(r => r.tenantName))];
  console.log("Tenants in dropdown:", tenants);

  return (
    <div style={containerStyle}>
      <div>
        <Header title="EC2 Snapshots" subtitle={`${org}`} />
      </div>

      {/* Dropdown only if role is superadmin */}
      {role === "SuperAdmin" && (
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0" }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel></InputLabel>
            <Select
              value={tenantName}
              onChange={(e) => settenantName(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    // force vertical stacking
                    '& .MuiList-root': {
                      display: 'flex !important',
                      flexDirection: 'column !important',
                    },
                    // force left-aligned text inside items
                    '& .MuiMenuItem-root': {
                      justifyContent: 'flex-start',
                      pl: 2,
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All Tenants</MenuItem>
              {tenantsFromDb.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.name}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )} 

      {loading ? (<Loader />) : (
        <div>
          <div className="ag-theme-quartz resourcecost" style={{ ...gridStyle, marginTop: 10 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              autoGroupColumnDef={autoGroupColumnDef}
              rowGroupPanelShow={"always"}
              sideBar={sideBar}
              defaultColDef={defaultColDef}
              groupIncludeFooter={true}
              groupIncludeTotalFooter={true}
              groupDefaultExpanded={-1}
              suppressAggFuncInHeader={true}
              gridOptions={gridOptions}
            />
          </div>
          <div className="recommended-action-container">
            <div className="scroll-container">
              <strong>Recommended Action</strong>
              <ul>
                <li>
                  <strong>Transition Snapshots to Lower-Cost Storage</strong>
                  <p>EBS snapshots are incremental, and deleting older snapshots may move costs to more recent snapshots if they reference the deleted data.</p>
                  <ul>
                    <li>Delete Unneeded Snapshots: Remove snapshots that are no longer necessary for volume recovery.</li>
                    <li>Move to Lower Storage Class: Transition infrequently accessed snapshots to a lower-cost archival storage class if applicable.</li>
                  </ul>
                  <p>Considerations:</p>
                  <ul>
                    <li>Data Retention: Deleting a snapshot may shift the cost to later snapshots if they reference the deleted snapshot’s data.</li>
                    <li>Snapshot Dependencies: Ensure that no critical data is lost, as referenced data in deleted snapshots may be moved to later snapshots.</li>
                    <li>Deletion Limitations: Some snapshots may be protected by Recycle Bin rules or be part of disabled AMIs or shared snapshots, affecting their deletion.</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
          <div className="note-container">
            <p>
              <strong>Note*</strong><span className="note-text">: The potential cost savings are calculated by comparing the cost of standard snapshot storage with archival storage. The monthly snapshot storage cost is computed by multiplying the snapshot size (in GB) by the standard storage rate ($0.05 per GB). The archival cost is similarly calculated at a lower rate ($0.0125 per GB). Savings are determined by subtracting the archival cost from the standard storage cost, showing how much could be saved by moving snapshots to a cheaper archival tier.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default EC2Snapshots;
